import { useState, useEffect, useCallback } from "react";
import { Plus, Search, Trash2, Edit3, Mail, Send, Loader2, Sparkles, Users, FileText, Link2, ChevronRight, Activity, Dumbbell, Apple, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { useLanguage } from "@/contexts/LanguageContext";
import { ClientDetailPanel } from "@/components/crm/ClientDetailPanel";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CrmPage({ onLogout }) {
  const { lang, t } = useLanguage();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [clients, setClients] = useState([]);
  const [bioClients, setBioClients] = useState([]);
  const [trainingPlans, setTrainingPlans] = useState([]);
  const [nutritionPlans, setNutritionPlans] = useState([]);
  const [anamneseRecords, setAnamneseRecords] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Client modal
  const [clientModal, setClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientForm, setClientForm] = useState({ vorname: "", nachname: "", geschlecht: "", groesse: "", geburtsdatum: "", adresse: "", telefon: "", email: "", notizen: "" });

  // Email compose
  const [emailModal, setEmailModal] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [generatingAi, setGeneratingAi] = useState(false);
  const [sending, setSending] = useState(false);

  // Template modal
  const [templateModal, setTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: "", subject: "", body: "", is_automated: false, trigger_type: null, days_before: 3 });

  // Link bio modal
  const [linkModal, setLinkModal] = useState(false);
  const [linkClientId, setLinkClientId] = useState(null);
  const [detailClient, setDetailClient] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [clientsRes, bioRes, tmplRes, logsRes, tpRes, npRes, anRes] = await Promise.all([
        axios.get(`${API}/api/crm/clients${search ? `?search=${search}` : ''}`, { headers }),
        axios.get(`${API}/api/bio/clients`, { headers }).catch(() => ({ data: { clients: [] } })),
        axios.get(`${API}/api/crm/email-templates`, { headers }),
        axios.get(`${API}/api/crm/email-logs`, { headers }),
        axios.get(`${API}/api/training-plans`, { headers }).catch(() => ({ data: { plans: [] } })),
        axios.get(`${API}/api/nutrition-plans`, { headers }).catch(() => ({ data: { plans: [] } })),
        axios.get(`${API}/api/anamnese/records`, { headers }).catch(() => ({ data: { records: [] } })),
      ]);
      setClients(clientsRes.data.clients || []);
      setBioClients(bioRes.data.clients || []);
      setTemplates(tmplRes.data.templates || []);
      setEmailLogs(logsRes.data.logs || []);
      setTrainingPlans(tpRes.data.plans || []);
      setNutritionPlans(npRes.data.plans || []);
      setAnamneseRecords(anRes.data.records || []);
    } catch { toast.error(t("error")); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { loadData(); }, [loadData]);

  // Client handlers
  const openNewClient = () => {
    setEditingClient(null);
    setClientForm({ vorname: "", nachname: "", geschlecht: "", groesse: "", geburtsdatum: "", adresse: "", telefon: "", email: "", notizen: "" });
    setClientModal(true);
  };

  const openEditClient = (c) => {
    setEditingClient(c);
    setClientForm({ vorname: c.vorname, nachname: c.nachname, geschlecht: c.geschlecht || "", groesse: c.groesse || "", geburtsdatum: c.geburtsdatum || "", adresse: c.adresse || "", telefon: c.telefon || "", email: c.email || "", notizen: c.notizen || "" });
    setClientModal(true);
  };

  const saveClient = async (e) => {
    e.preventDefault();
    if (!clientForm.vorname || !clientForm.nachname) { toast.error(t("error")); return; }
    try {
      const payload = { ...clientForm, groesse: clientForm.groesse ? parseFloat(clientForm.groesse) : null };
      if (editingClient) await axios.put(`${API}/api/crm/clients/${editingClient.id}`, payload, { headers });
      else await axios.post(`${API}/api/crm/clients`, payload, { headers });
      toast.success(t("success")); setClientModal(false); loadData();
    } catch { toast.error(t("error")); }
  };

  const deleteClient = async (id) => {
    try { await axios.delete(`${API}/api/crm/clients/${id}`, { headers }); toast.success(t("success")); loadData(); }
    catch { toast.error(t("error")); }
  };

  const linkBio = async (bioId) => {
    try { await axios.post(`${API}/api/crm/clients/${linkClientId}/link-bio/${bioId}`, {}, { headers }); toast.success(t("success")); setLinkModal(false); loadData(); }
    catch { toast.error(t("error")); }
  };

  // Email handlers
  const toggleSelectClient = (id) => {
    setSelectedClients(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  };

  const selectAll = () => {
    const withEmail = clients.filter(c => c.email).map(c => c.id);
    setSelectedClients(prev => prev.length === withEmail.length ? [] : withEmail);
  };

  const generateWithAi = async () => {
    if (!aiPrompt.trim()) return;
    setGeneratingAi(true);
    try {
      const clientName = selectedClients.length === 1 ? clients.find(c => c.id === selectedClients[0])?.vorname : null;
      const res = await axios.post(`${API}/api/crm/generate-email`, { prompt: aiPrompt, client_name: clientName, language: lang }, { headers });
      setEmailSubject(res.data.subject || "");
      setEmailBody(res.data.body || "");
      toast.success(t("success"));
    } catch { toast.error(t("error")); }
    finally { setGeneratingAi(false); }
  };

  const sendEmails = async () => {
    if (!selectedClients.length || !emailSubject.trim()) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/crm/send-emails`, { client_ids: selectedClients, subject: emailSubject, body: emailBody }, { headers });
      toast.success(res.data.message); setEmailModal(false); setSelectedClients([]); setEmailSubject(""); setEmailBody(""); loadData();
    } catch { toast.error(t("error")); }
    finally { setSending(false); }
  };

  // Template handlers
  const openNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({ name: "", subject: "", body: "", is_automated: false, trigger_type: null, days_before: 3 });
    setTemplateModal(true);
  };

  const saveTemplate = async (e) => {
    e.preventDefault();
    try {
      if (editingTemplate) await axios.put(`${API}/api/crm/email-templates/${editingTemplate.id}`, templateForm, { headers });
      else await axios.post(`${API}/api/crm/email-templates`, templateForm, { headers });
      toast.success(t("success")); setTemplateModal(false); loadData();
    } catch { toast.error(t("error")); }
  };

  const applyTemplate = (tmpl) => {
    setEmailSubject(tmpl.subject);
    setEmailBody(tmpl.body);
    setEmailModal(true);
  };

  return (
    <Layout onLogout={onLogout}>
      <div className="p-6 h-full overflow-y-auto" data-testid="crm-page">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{t("crm_title")}</h1>
              <p className="text-sm text-slate-600">{t("crm_subtitle")}</p>
            </div>
          </div>

          <Tabs defaultValue="clients">
            <TabsList className="mb-6">
              <TabsTrigger value="clients" data-testid="tab-clients"><Users className="w-4 h-4 mr-2" />{t("clients_tab")}</TabsTrigger>
              <TabsTrigger value="emails" data-testid="tab-emails"><Mail className="w-4 h-4 mr-2" />{t("emails_tab")}</TabsTrigger>
              <TabsTrigger value="templates" data-testid="tab-templates"><FileText className="w-4 h-4 mr-2" />{t("templates_tab")}</TabsTrigger>
            </TabsList>

            {/* CLIENTS TAB */}
            <TabsContent value="clients">
              <div className="flex items-center gap-3 mb-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search")} className="pl-9" data-testid="crm-search" />
                </div>
                <Button onClick={() => { if (selectedClients.length) { setEmailModal(true); } else toast.error(t("select_all")); }} variant="outline" disabled={!selectedClients.length}>
                  <Mail className="w-4 h-4 mr-2" />{t("send_email")} ({selectedClients.length})
                </Button>
                <Button onClick={openNewClient} data-testid="add-client-btn"><Plus className="w-4 h-4 mr-2" />{t("add_client_crm")}</Button>
              </div>

              {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
              ) : clients.length === 0 ? (
                <div className="text-center py-12 bg-white border rounded-lg">
                  <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">{t("no_clients")}</p>
                  <Button onClick={openNewClient} className="mt-4"><Plus className="w-4 h-4 mr-2" />{t("add_client_crm")}</Button>
                </div>
              ) : (
                <div className="bg-white border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="w-10 px-3 py-3"><Checkbox checked={selectedClients.length === clients.filter(c => c.email).length && selectedClients.length > 0} onCheckedChange={selectAll} /></th>
                        <th className="px-3 py-3 text-left font-medium text-slate-700">{t("name")}</th>
                        <th className="px-3 py-3 text-left font-medium text-slate-700">{t("email")}</th>
                        <th className="px-3 py-3 text-left font-medium text-slate-700">Bio</th>
                        <th className="px-3 py-3 text-left font-medium text-slate-700">Training</th>
                        <th className="px-3 py-3 text-left font-medium text-slate-700">Ernährung</th>
                        <th className="px-3 py-3 text-left font-medium text-slate-700">Anamnese</th>
                        <th className="px-3 py-3 text-right font-medium text-slate-700">{t("actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clients.map(c => (
                        <tr key={c.id} className="border-b last:border-0 hover:bg-slate-50 transition-colors" data-testid={`crm-client-${c.id}`}>
                          <td className="px-3 py-2.5">
                            {c.email ? <Checkbox checked={selectedClients.includes(c.id)} onCheckedChange={() => toggleSelectClient(c.id)} /> : <span className="text-[10px] text-slate-400">{t("no_email")}</span>}
                          </td>
                          <td className="px-3 py-2.5">
                            <p className="font-medium text-slate-900 cursor-pointer hover:text-blue-700 transition-colors" onClick={() => setDetailClient(c)} data-testid={`client-name-${c.id}`}>{c.nachname}, {c.vorname}</p>
                            {c.telefon && <p className="text-[10px] text-slate-400">{c.telefon}</p>}
                          </td>
                          <td className="px-3 py-2.5 text-slate-600 text-xs">{c.email || "-"}</td>
                          <td className="px-3 py-2.5">
                            {c.has_bio_data ? (
                              <Badge className="bg-emerald-100 text-emerald-700 text-[10px]"><Activity className="w-3 h-3 mr-1" />{t("linked_bio")}</Badge>
                            ) : (
                              <button onClick={() => { setLinkClientId(c.id); setLinkModal(true); }} className="text-[10px] text-violet-600 hover:underline flex items-center gap-0.5">
                                <Link2 className="w-3 h-3" />{t("link_bio")}
                              </button>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            {(() => {
                              const tp = trainingPlans.filter(p => p.client_id === c.id);
                              return tp.length > 0 ? (
                                <div className="space-y-0.5">{tp.map(p => (
                                  <Badge key={p.id} className="bg-amber-100 text-amber-700 text-[10px] block w-fit cursor-pointer hover:bg-amber-200" onClick={() => window.location.href = '/training-plans'}>
                                    <Dumbbell className="w-3 h-3 mr-1 inline" />{p.name.length > 15 ? p.name.slice(0,15)+'...' : p.name}
                                  </Badge>
                                ))}</div>
                              ) : <span className="text-[10px] text-slate-400">—</span>;
                            })()}
                          </td>
                          <td className="px-3 py-2.5">
                            {(() => {
                              const np = nutritionPlans.filter(p => p.client_id === c.id);
                              return np.length > 0 ? (
                                <div className="space-y-0.5">{np.map(p => (
                                  <Badge key={p.id} className="bg-emerald-100 text-emerald-700 text-[10px] block w-fit cursor-pointer hover:bg-emerald-200" onClick={() => window.location.href = '/nutrition'}>
                                    <Apple className="w-3 h-3 mr-1 inline" />{p.name.length > 15 ? p.name.slice(0,15)+'...' : p.name}
                                  </Badge>
                                ))}</div>
                              ) : <span className="text-[10px] text-slate-400">—</span>;
                            })()}
                          </td>
                          <td className="px-3 py-2.5">
                            {(() => {
                              const fullName = `${c.vorname} ${c.nachname}`.toLowerCase();
                              const ar = anamneseRecords.filter(r => (r.client_name || '').toLowerCase().includes(fullName) || fullName.includes((r.client_name || '').toLowerCase()));
                              return ar.length > 0 ? (
                                <Badge className="bg-violet-100 text-violet-700 text-[10px] cursor-pointer hover:bg-violet-200" onClick={() => window.location.href = '/anamnese'}>
                                  <ClipboardList className="w-3 h-3 mr-1 inline" />{ar.length}
                                </Badge>
                              ) : <span className="text-[10px] text-slate-400">—</span>;
                            })()}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => openEditClient(c)} className="p-1.5 rounded hover:bg-slate-100"><Edit3 className="w-3.5 h-3.5 text-slate-500" /></button>
                              <button onClick={() => deleteClient(c.id)} className="p-1.5 rounded hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            {/* EMAILS TAB */}
            <TabsContent value="emails">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-semibold text-slate-900">{t("email_logs")}</h2>
                <Button onClick={() => setEmailModal(true)}><Mail className="w-4 h-4 mr-2" />{t("send_email")}</Button>
              </div>
              {emailLogs.length === 0 ? (
                <div className="text-center py-12 bg-white border rounded-lg">
                  <Mail className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">{t("no_data")}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {emailLogs.map(log => (
                    <div key={log.id} className="bg-white border rounded-lg p-4 flex items-center gap-4">
                      <Mail className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{log.subject}</p>
                        <p className="text-xs text-slate-500">{log.client_name} &middot; {log.client_email}</p>
                      </div>
                      <Badge variant="outline" className="text-[10px]">{log.status === "queued" ? t("queued") : t("sent")}</Badge>
                      <span className="text-[10px] text-slate-400">{new Date(log.created_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* TEMPLATES TAB */}
            <TabsContent value="templates">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-semibold text-slate-900">{t("email_templates")}</h2>
                <Button onClick={openNewTemplate}><Plus className="w-4 h-4 mr-2" />{t("new_template")}</Button>
              </div>
              {templates.length === 0 ? (
                <div className="text-center py-12 bg-white border rounded-lg">
                  <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">{t("no_data")}</p>
                  <Button onClick={openNewTemplate} className="mt-4"><Plus className="w-4 h-4 mr-2" />{t("new_template")}</Button>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {templates.map(tmpl => (
                    <div key={tmpl.id} className="bg-white border rounded-lg p-4 hover:shadow-md transition-all">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium text-slate-900 text-sm">{tmpl.name}</h3>
                        {tmpl.is_automated && <Badge className="bg-violet-100 text-violet-700 text-[10px]">{t("automated")}</Badge>}
                      </div>
                      <p className="text-xs text-slate-500 truncate mb-3">{tmpl.subject}</p>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="flex-1" onClick={() => applyTemplate(tmpl)}><Send className="w-3 h-3 mr-1" />{t("send_email")}</Button>
                        <button onClick={() => { setEditingTemplate(tmpl); setTemplateForm({ name: tmpl.name, subject: tmpl.subject, body: tmpl.body, is_automated: tmpl.is_automated, trigger_type: tmpl.trigger_type, days_before: tmpl.days_before || 3 }); setTemplateModal(true); }} className="p-1.5 rounded hover:bg-slate-100">
                          <Edit3 className="w-3.5 h-3.5 text-slate-500" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Client Modal */}
      <Dialog open={clientModal} onOpenChange={setClientModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingClient ? t("edit_client_crm") : t("add_client_crm")}</DialogTitle></DialogHeader>
          <form onSubmit={saveClient} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t("first_name")} *</Label><Input value={clientForm.vorname} onChange={(e) => setClientForm({ ...clientForm, vorname: e.target.value })} data-testid="crm-vorname" /></div>
              <div><Label>{t("last_name")} *</Label><Input value={clientForm.nachname} onChange={(e) => setClientForm({ ...clientForm, nachname: e.target.value })} data-testid="crm-nachname" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>{t("gender")}</Label>
                <Select value={clientForm.geschlecht || "none"} onValueChange={(v) => setClientForm({ ...clientForm, geschlecht: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-</SelectItem>
                    <SelectItem value="m">{t("gender_m")}</SelectItem>
                    <SelectItem value="f">{t("gender_f")}</SelectItem>
                    <SelectItem value="d">{t("gender_d")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{t("height")}</Label><Input type="number" value={clientForm.groesse} onChange={(e) => setClientForm({ ...clientForm, groesse: e.target.value })} /></div>
              <div><Label>{t("date_of_birth")}</Label><Input type="date" value={clientForm.geburtsdatum} onChange={(e) => setClientForm({ ...clientForm, geburtsdatum: e.target.value })} /></div>
            </div>
            <div><Label>{t("email")}</Label><Input type="email" value={clientForm.email} onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })} data-testid="crm-email" /></div>
            <div><Label>{t("phone")}</Label><Input value={clientForm.telefon} onChange={(e) => setClientForm({ ...clientForm, telefon: e.target.value })} /></div>
            <div><Label>{t("address")}</Label><Input value={clientForm.adresse} onChange={(e) => setClientForm({ ...clientForm, adresse: e.target.value })} /></div>
            <div><Label>{t("notes_field")}</Label><Textarea value={clientForm.notizen} onChange={(e) => setClientForm({ ...clientForm, notizen: e.target.value })} rows={2} /></div>
            <DialogFooter className="flex justify-between">
              {editingClient && <Button type="button" variant="destructive" onClick={() => { deleteClient(editingClient.id); setClientModal(false); }}>{t("delete")}</Button>}
              <div className="flex gap-2 ml-auto">
                <Button type="button" variant="outline" onClick={() => setClientModal(false)}>{t("cancel")}</Button>
                <Button type="submit" data-testid="crm-save-client">{editingClient ? t("save") : t("create")}</Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Email Compose Modal */}
      <Dialog open={emailModal} onOpenChange={setEmailModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle><Mail className="w-5 h-5 inline mr-2" />{t("send_email")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-slate-500">{selectedClients.length} {t("clients_tab")} {t("select_all").toLowerCase()}</div>
            {/* AI Generation */}
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4 text-violet-500" /><span className="text-sm font-medium text-violet-800">{t("generate_with_ai")}</span></div>
              <div className="flex gap-2">
                <Input value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} placeholder={t("ai_prompt")} className="flex-1" data-testid="ai-email-prompt"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); generateWithAi(); } }}
                />
                <Button onClick={generateWithAi} disabled={generatingAi || !aiPrompt.trim()} variant="outline" data-testid="ai-generate-btn">
                  {generatingAi ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div><Label>{t("subject")}</Label><Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} data-testid="email-subject" /></div>
            <div><Label>{t("body")}</Label><Textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={8} data-testid="email-body" /></div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailModal(false)}>{t("cancel")}</Button>
              <Button onClick={sendEmails} disabled={sending || !emailSubject.trim()} data-testid="send-email-btn">
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {t("send_to_selected")}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Modal */}
      <Dialog open={templateModal} onOpenChange={setTemplateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingTemplate ? t("edit_template") : t("new_template")}</DialogTitle></DialogHeader>
          <form onSubmit={saveTemplate} className="space-y-4">
            <div><Label>{t("template_name")}</Label><Input value={templateForm.name} onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })} /></div>
            <div><Label>{t("subject")}</Label><Input value={templateForm.subject} onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })} /></div>
            <div><Label>{t("body")}</Label><Textarea value={templateForm.body} onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })} rows={6} /></div>
            <div className="flex items-center gap-2">
              <Checkbox id="tmpl-auto" checked={templateForm.is_automated} onCheckedChange={(c) => setTemplateForm({ ...templateForm, is_automated: c })} />
              <Label htmlFor="tmpl-auto">{t("automated")}</Label>
            </div>
            {templateForm.is_automated && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t("trigger_type")}</Label>
                  <Select value={templateForm.trigger_type || "custom"} onValueChange={(v) => setTemplateForm({ ...templateForm, trigger_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="birthday">{t("trigger_birthday")}</SelectItem>
                      <SelectItem value="assessment">{t("trigger_assessment")}</SelectItem>
                      <SelectItem value="welcome">{t("trigger_welcome")}</SelectItem>
                      <SelectItem value="custom">{t("trigger_custom")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>{t("days_before")}</Label><Input type="number" value={templateForm.days_before} onChange={(e) => setTemplateForm({ ...templateForm, days_before: parseInt(e.target.value) || 0 })} /></div>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTemplateModal(false)}>{t("cancel")}</Button>
              <Button type="submit">{editingTemplate ? t("save") : t("create")}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Link Bio Modal */}
      <Dialog open={linkModal} onOpenChange={setLinkModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("link_bio")}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {bioClients.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">{t("no_data")}</p>
            ) : bioClients.map(bc => (
              <button key={bc.id} onClick={() => linkBio(bc.id)} className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-slate-50 transition-colors text-left">
                <Activity className="w-5 h-5 text-emerald-500" />
                <div>
                  <p className="text-sm font-medium">{bc.vorname} {bc.nachname}</p>
                  <p className="text-xs text-slate-500">{bc.geburtsdatum}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400 ml-auto" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      {/* Client Detail Panel */}
      {detailClient && (
        <ClientDetailPanel client={detailClient} onClose={() => setDetailClient(null)}
          trainingPlans={trainingPlans} nutritionPlans={nutritionPlans} anamneseRecords={anamneseRecords} />
      )}
    </Layout>
  );
}
